import moment from 'moment-timezone';
import timezoneOffsetLabel from '@customerio/fly-components/utils/timezone-offset-label';

// @ts-ignore: "ember-get-config" version that we're using is not typed
import config from 'ember-get-config';

// Ember.testing flag cannot be used to detect testing environment on buildtime, in util module: https://github.com/emberjs/ember.js/issues/16006#issuecomment-352538098
let isTestingEnvironment = config.environment === 'test';
let userHasDST = isTestingEnvironment ? false : moment().isDST();
// Shared list of selectable timezones in Customer.io app input components
//
// originally sourced from https://github.com/dmfilipenko/timezones.json , with manual transformations applied: most important of which is merging DST and non-DST timezones into a single object, with "observesDST: true" property.
//
// Current mirrored version: https://github.com/dmfilipenko/timezones.json/blob/0a71abd6c4857773fe402a76e251591bd560ab01/timezones.json
//
// To update this file:
// 1. Reapply latest commits from: https://github.com/dmfilipenko/timezones.json since the last 'Current Mirrored version' set above
// 2. Update the 'Current Mirrored version' link above to point to the latest applied revision.
//
// Alternatively, it's possible that "moment-timezone" timezones DB change wasn't reflected in the "timezones.json" file yet. In this case our "time-zones" util tests will start failing.
// If this happens, verify correct TZ data and apply the required change by hand.

// It's sorted:
// 1. By hand: most common customer timezones being visible at the beginning
// 2. To always include user's timezone as first entry (see later transformation of the array)
let RAW_TIME_ZONES = [{
  offset: -8,
  observesDST: true,
  label: 'Pacific Time (US & Canada)',
  value: 'US/Pacific',
  matchingValues: ['US/Pacific', 'America/Los_Angeles', 'America/Tijuana', 'America/Vancouver'],
  name: 'Pacific Standard Time'
}, {
  offset: -7,
  observesDST: true,
  label: 'Mountain Time (US & Canada)',
  value: 'US/Mountain',
  matchingValues: ['US/Mountain', 'America/Boise', 'America/Cambridge_Bay', 'America/Denver', 'America/Edmonton', 'America/Inuvik', 'America/Yellowknife', 'MST7MDT'],
  name: 'Mountain Standard Time'
}, {
  offset: -7,
  observesDST: false,
  label: 'Mountain Time (Canada)',
  value: 'America/Dawson',
  matchingValues: ['America/Dawson', 'America/Whitehorse'],
  name: 'Canada Mountain Standard Time'
}, {
  offset: -6,
  observesDST: true,
  label: 'Central Time (US & Canada)',
  value: 'US/Central',
  matchingValues: ['US/Central', 'America/Chicago', 'America/Indiana/Knox', 'America/Indiana/Tell_City', 'America/Matamoros', 'America/Menominee', 'America/North_Dakota/Beulah', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem', 'America/Rainy_River', 'America/Rankin_Inlet', 'America/Resolute', 'America/Winnipeg', 'America/Ojinaga', 'CST6CDT'],
  name: 'Central Standard Time'
}, {
  offset: -5,
  observesDST: true,
  label: 'Eastern Time (US & Canada)',
  value: 'US/Eastern',
  matchingValues: ['US/Eastern', 'America/Grand_Turk', 'America/Detroit', 'America/Havana', 'America/Indiana/Petersburg', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Iqaluit', 'America/Kentucky/Monticello', 'America/Louisville', 'America/Montreal', 'America/Nassau', 'America/New_York', 'America/Nipigon', 'America/Pangnirtung', 'America/Port-au-Prince', 'America/Thunder_Bay', 'America/Toronto', 'EST5EDT'],
  name: 'Eastern Standard Time'
}, {
  offset: -12,
  observesDST: false,
  label: 'International Date Line West',
  value: 'Etc/GMT+12',
  matchingValues: ['Etc/GMT+12'],
  name: 'Dateline Standard Time'
}, {
  offset: -11,
  observesDST: false,
  label: 'Midway Island, American Samoa',
  value: 'Etc/GMT+11',
  matchingValues: ['Etc/GMT+11', 'Pacific/Midway', 'Pacific/Niue', 'Pacific/Pago_Pago'],
  name: 'Samoa Time Zone'
}, {
  offset: -10,
  observesDST: false,
  label: 'Hawaii',
  value: 'Etc/GMT+10',
  matchingValues: ['Etc/GMT+10', 'Pacific/Honolulu', 'Pacific/Johnston', 'Pacific/Rarotonga', 'Pacific/Tahiti'],
  name: 'Hawaiian Standard Time'
}, {
  offset: -9,
  observesDST: true,
  label: 'Alaska',
  value: 'America/Anchorage',
  matchingValues: ['America/Anchorage', 'America/Juneau', 'America/Nome', 'America/Sitka', 'America/Yakutat'],
  name: 'Alaskan Standard Time'
}, {
  offset: -8,
  observesDST: true,
  label: 'Baja California',
  value: 'America/Santa_Isabel',
  matchingValues: ['America/Santa_Isabel'],
  name: 'Pacific Standard Time (Mexico)'
}, {
  offset: -7,
  observesDST: false,
  label: 'Arizona',
  value: 'Etc/GMT+7',
  matchingValues: ['America/Creston', 'America/Dawson_Creek', 'America/Hermosillo', 'America/Phoenix', 'America/Whitehorse', 'Etc/GMT+7'],
  name: 'US Mountain Standard Time'
}, {
  offset: -7,
  observesDST: false,
  label: 'Mazatlan',
  value: 'America/Mazatlan',
  matchingValues: ['America/Mazatlan'],
  name: 'Mountain Standard Time (Mexico)'
}, {
  offset: -6,
  observesDST: false,
  label: 'Central America',
  value: 'Etc/GMT+6',
  matchingValues: ['America/Belize', 'America/Costa_Rica', 'America/El_Salvador', 'America/Guatemala', 'America/Managua', 'America/Tegucigalpa', 'Etc/GMT+6', 'Pacific/Galapagos'],
  name: 'Central America Standard Time'
}, {
  offset: -6,
  observesDST: false,
  label: 'Guadalajara, Mexico City, Monterrey, Chihuahua',
  value: 'America/Bahia_Banderas',
  matchingValues: ['America/Bahia_Banderas', 'America/Merida', 'America/Mexico_City', 'America/Monterrey', 'America/Chihuahua'],
  name: 'Central Standard Time (Mexico)'
}, {
  offset: -5,
  observesDST: false,
  label: 'Cancun',
  value: 'America/Cancun',
  matchingValues: ['America/Cancun'],
  name: 'Eastern Standard Time (Mexico)'
}, {
  offset: -6,
  observesDST: false,
  label: 'Saskatchewan',
  value: 'America/Regina',
  matchingValues: ['America/Regina', 'America/Swift_Current'],
  name: 'Canada Central Standard Time'
}, {
  offset: -5,
  observesDST: false,
  label: 'Bogota, Lima, Quito',
  value: 'Etc/GMT+5',
  matchingValues: ['America/Bogota', 'America/Cayman', 'America/Coral_Harbour', 'America/Eirunepe', 'America/Guayaquil', 'America/Jamaica', 'America/Lima', 'America/Panama', 'America/Rio_Branco', 'Etc/GMT+5'],
  name: 'SA Pacific Standard Time'
}, {
  offset: -5,
  observesDST: true,
  label: 'Indiana (East)',
  value: 'America/Indiana/Marengo',
  matchingValues: ['America/Indiana/Marengo', 'America/Indiana/Vevay', 'America/Indianapolis'],
  name: 'US Eastern Standard Time'
}, {
  offset: -4,
  observesDST: false,
  label: 'Caracas',
  value: 'America/Caracas',
  matchingValues: ['America/Caracas'],
  name: 'Venezuela Standard Time'
}, {
  offset: -4,
  observesDST: true,
  label: 'Asuncion',
  value: 'America/Asuncion',
  matchingValues: ['America/Asuncion'],
  name: 'Paraguay Standard Time'
}, {
  offset: -4,
  observesDST: true,
  label: 'Atlantic Time (Canada)',
  value: 'America/Glace_Bay',
  matchingValues: ['America/Glace_Bay', 'America/Goose_Bay', 'America/Halifax', 'America/Moncton', 'America/Thule', 'Atlantic/Bermuda'],
  name: 'Atlantic Standard Time'
}, {
  offset: -4,
  observesDST: false,
  label: 'Cuiaba',
  value: 'America/Campo_Grande',
  matchingValues: ['America/Campo_Grande', 'America/Cuiaba'],
  name: 'Central Brazilian Standard Time'
}, {
  offset: -4,
  observesDST: false,
  label: 'Georgetown, La Paz, Manaus, San Juan',
  value: 'Etc/GMT+4',
  matchingValues: ['America/Anguilla', 'America/Antigua', 'America/Aruba', 'America/Barbados', 'America/Blanc-Sablon', 'America/Boa_Vista', 'America/Curacao', 'America/Dominica', 'America/Grenada', 'America/Guadeloupe', 'America/Guyana', 'America/Kralendijk', 'America/La_Paz', 'America/Lower_Princes', 'America/Manaus', 'America/Marigot', 'America/Martinique', 'America/Montserrat', 'America/Port_of_Spain', 'America/Porto_Velho', 'America/Puerto_Rico', 'America/Santo_Domingo', 'America/St_Barthelemy', 'America/St_Kitts', 'America/St_Lucia', 'America/St_Thomas', 'America/St_Vincent', 'America/Tortola', 'Etc/GMT+4'],
  name: 'SA Western Standard Time'
}, {
  offset: -4,
  observesDST: true,
  label: 'Santiago',
  value: 'America/Santiago',
  matchingValues: ['America/Santiago'],
  name: 'Pacific SA Standard Time'
}, {
  offset: -3.5,
  observesDST: true,
  label: 'Newfoundland',
  value: 'America/St_Johns',
  matchingValues: ['America/St_Johns'],
  name: 'Newfoundland Standard Time'
}, {
  offset: -3,
  observesDST: false,
  label: 'Brasilia',
  value: 'America/Sao_Paulo',
  matchingValues: ['America/Sao_Paulo'],
  name: 'E. South America Standard Time'
}, {
  offset: -3,
  observesDST: false,
  label: 'Buenos Aires',
  value: 'America/Argentina/La_Rioja',
  matchingValues: ['America/Argentina/Buenos_Aires', 'America/Argentina/Catamarca', 'America/Argentina/Cordoba', 'America/Argentina/Jujuy', 'America/Argentina/La_Rioja', 'America/Argentina/Mendoza', 'America/Argentina/Rio_Gallegos', 'America/Argentina/Salta', 'America/Argentina/San_Juan', 'America/Argentina/San_Luis', 'America/Argentina/Tucuman', 'America/Argentina/Ushuaia', 'America/Buenos_Aires', 'America/Catamarca', 'America/Cordoba', 'America/Jujuy', 'America/Mendoza'],
  name: 'Argentina Standard Time'
}, {
  offset: -3,
  observesDST: false,
  label: 'Cayenne, Fortaleza',
  value: 'Etc/GMT+3',
  matchingValues: ['America/Araguaina', 'America/Belem', 'America/Cayenne', 'America/Fortaleza', 'America/Maceio', 'America/Paramaribo', 'America/Recife', 'America/Santarem', 'Antarctica/Rothera', 'Atlantic/Stanley', 'Etc/GMT+3'],
  name: 'SA Eastern Standard Time'
}, {
  offset: -2,
  observesDST: true,
  label: 'Greenland',
  value: 'America/Godthab',
  matchingValues: ['America/Godthab'],
  name: 'Greenland Standard Time'
}, {
  offset: -3,
  observesDST: false,
  label: 'Montevideo',
  value: 'America/Montevideo',
  matchingValues: ['America/Montevideo'],
  name: 'Montevideo Standard Time'
}, {
  offset: -3,
  observesDST: false,
  label: 'Salvador',
  value: 'America/Bahia',
  matchingValues: ['America/Bahia'],
  name: 'Bahia Standard Time'
}, {
  offset: -2,
  observesDST: false,
  label: 'Fernando de Noronha',
  value: 'Etc/GMT+2',
  matchingValues: ['America/Noronha', 'Atlantic/South_Georgia', 'Etc/GMT+2'],
  name: 'Fernando de Noronha Time'
}, {
  offset: -4,
  observesDST: true,
  label: 'Mid-Atlantic',
  value: 'America/Halifax',
  matchingValues: ['America/Glace_Bay', 'America/Goose_Bay', 'America/Halifax', 'America/Moncton', 'America/Thule', 'Atlantic/Bermuda'],
  name: 'Mid-Atlantic Standard Time'
}, {
  offset: -1,
  observesDST: true,
  label: 'Azores',
  value: 'America/Scoresbysund',
  matchingValues: ['America/Scoresbysund', 'Atlantic/Azores'],
  name: 'Azores Standard Time'
}, {
  offset: -1,
  observesDST: false,
  label: 'Cape Verde Is.',
  value: 'Etc/GMT+1',
  matchingValues: ['Atlantic/Cape_Verde', 'Etc/GMT+1'],
  name: 'Cape Verde Standard Time'
}, {
  offset: 0,
  observesDST: false,
  label: 'Coordinated Universal Time',
  value: 'Etc/GMT',
  matchingValues: ['America/Danmarkshavn', 'Etc/GMT'],
  name: 'UTC'
},
// Morocco observes DST/WEST timezone year-round, except for dynamic Ramadan period
// more: https://www.timeanddate.com/news/time/morocco-changes-clocks-2019.html
// https://www.timeanddate.com/time/zone/morocco/casablanca
{
  offset: 0,
  observesDST: false,
  label: 'Casablanca',
  value: 'Africa/Casablanca',
  matchingValues: ['Africa/Casablanca', 'Africa/El_Aaiun'],
  name: 'Morocco Standard Time'
}, {
  offset: 0,
  observesDST: true,
  label: 'Dublin, Edinburgh, Lisbon, London',
  value: 'Atlantic/Canary',
  matchingValues: ['Atlantic/Canary', 'Atlantic/Faeroe', 'Atlantic/Madeira', 'Europe/Dublin', 'Europe/Lisbon'],
  name: 'GMT Standard Time'
}, {
  offset: 0,
  observesDST: false,
  label: 'Monrovia, Reykjavik',
  value: 'Africa/Abidjan',
  matchingValues: ['Africa/Abidjan', 'Africa/Accra', 'Africa/Bamako', 'Africa/Banjul', 'Africa/Bissau', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Freetown', 'Africa/Lome', 'Africa/Monrovia', 'Africa/Nouakchott', 'Africa/Ouagadougou', 'Africa/Sao_Tome', 'Atlantic/Reykjavik', 'Atlantic/St_Helena'],
  name: 'Greenwich Standard Time'
}, {
  offset: 1,
  observesDST: true,
  label: 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  value: 'Arctic/Longyearbyen',
  matchingValues: ['Arctic/Longyearbyen', 'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Berlin', 'Europe/Busingen', 'Europe/Gibraltar', 'Europe/Luxembourg', 'Europe/Malta', 'Europe/Monaco', 'Europe/Oslo', 'Europe/Rome', 'Europe/San_Marino', 'Europe/Stockholm', 'Europe/Vaduz', 'Europe/Vatican', 'Europe/Vienna', 'Europe/Zurich'],
  name: 'W. Europe Standard Time'
}, {
  offset: 1,
  observesDST: true,
  label: 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
  value: 'Europe/Belgrade',
  matchingValues: ['Europe/Belgrade', 'Europe/Bratislava', 'Europe/Budapest', 'Europe/Ljubljana', 'Europe/Podgorica', 'Europe/Prague', 'Europe/Tirane'],
  name: 'Central Europe Standard Time'
}, {
  offset: 1,
  observesDST: true,
  label: 'Brussels, Copenhagen, Madrid, Paris',
  value: 'Africa/Ceuta',
  matchingValues: ['Africa/Ceuta', 'Europe/Brussels', 'Europe/Copenhagen', 'Europe/Madrid', 'Europe/Paris'],
  name: 'Romance Standard Time'
}, {
  offset: 1,
  observesDST: true,
  label: 'Sarajevo, Skopje, Warsaw, Zagreb',
  value: 'Europe/Sarajevo',
  matchingValues: ['Europe/Sarajevo', 'Europe/Skopje', 'Europe/Warsaw', 'Europe/Zagreb'],
  name: 'Central European Standard Time'
}, {
  offset: 1,
  observesDST: false,
  label: 'West Central Africa',
  value: 'Etc/GMT-1',
  matchingValues: ['Africa/Algiers', 'Africa/Bangui', 'Africa/Brazzaville', 'Africa/Douala', 'Africa/Kinshasa', 'Africa/Lagos', 'Africa/Libreville', 'Africa/Luanda', 'Africa/Malabo', 'Africa/Ndjamena', 'Africa/Niamey', 'Africa/Porto-Novo', 'Africa/Tunis', 'Etc/GMT-1'],
  name: 'W. Central Africa Standard Time'
}, {
  offset: 2,
  observesDST: false,
  label: 'Windhoek, Khartoum',
  value: 'Africa/Windhoek',
  matchingValues: ['Africa/Windhoek', 'Africa/Juba', 'Africa/Khartoum'],
  name: 'Central Africa Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'Athens, Bucharest',
  value: 'Asia/Nicosia',
  matchingValues: ['Asia/Nicosia', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Chisinau'],
  name: 'GTB Standard Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'Beirut',
  value: 'Asia/Beirut',
  matchingValues: ['Asia/Beirut'],
  name: 'Middle East Standard Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'Cairo',
  value: 'Africa/Cairo',
  matchingValues: ['Africa/Cairo'],
  name: 'Egypt Standard Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'E. Europe',
  value: 'Asia/Nicosia',
  matchingValues: ['Asia/Nicosia', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Chisinau'],
  name: 'E. Europe Standard Time'
}, {
  offset: 2,
  observesDST: false,
  label: 'Harare, Pretoria',
  value: 'Etc/GMT-2',
  matchingValues: ['Africa/Blantyre', 'Africa/Bujumbura', 'Africa/Gaborone', 'Africa/Harare', 'Africa/Johannesburg', 'Africa/Kigali', 'Africa/Lubumbashi', 'Africa/Lusaka', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane', 'Etc/GMT-2'],
  name: 'South Africa Standard Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
  value: 'Europe/Helsinki',
  matchingValues: ['Asia/Nicosia', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Chisinau', 'Europe/Helsinki', 'Europe/Kyiv', 'Europe/Mariehamn', 'Europe/Nicosia', 'Europe/Riga', 'Europe/Sofia', 'Europe/Tallinn', 'Europe/Uzhgorod', 'Europe/Vilnius', 'Europe/Zaporozhye'],
  name: 'FLE Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Istanbul',
  value: 'Europe/Istanbul',
  matchingValues: ['Europe/Istanbul'],
  name: 'Turkey Standard Time'
}, {
  offset: 2,
  observesDST: true,
  label: 'Jerusalem',
  value: 'Asia/Jerusalem',
  matchingValues: ['Asia/Jerusalem'],
  name: 'Israel Standard Time'
}, {
  offset: 2,
  observesDST: false,
  label: 'Tripoli',
  value: 'Africa/Tripoli',
  matchingValues: ['Africa/Tripoli'],
  name: 'Libya Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Baghdad',
  value: 'Asia/Baghdad',
  matchingValues: ['Asia/Baghdad'],
  name: 'Arabic Standard Time'
}, {
  offset: 2,
  observesDST: false,
  label: 'Kaliningrad',
  value: 'Europe/Kaliningrad',
  matchingValues: ['Europe/Kaliningrad'],
  name: 'Kaliningrad Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Kuwait, Riyadh',
  value: 'Asia/Aden',
  matchingValues: ['Asia/Aden', 'Asia/Bahrain', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Riyadh'],
  name: 'Arab Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Nairobi',
  value: 'Etc/GMT-3',
  matchingValues: ['Africa/Addis_Ababa', 'Africa/Asmera', 'Africa/Dar_es_Salaam', 'Africa/Djibouti', 'Africa/Kampala', 'Africa/Mogadishu', 'Africa/Nairobi', 'Antarctica/Syowa', 'Etc/GMT-3', 'Indian/Antananarivo', 'Indian/Comoro', 'Indian/Mayotte'],
  name: 'E. Africa Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Amman',
  value: 'Asia/Amman',
  matchingValues: ['Asia/Amman'],
  name: 'Jordan Standard Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Moscow, St. Petersburg, Volgograd, Minsk',
  value: 'Europe/Kirov',
  matchingValues: ['Europe/Kirov', 'Europe/Moscow', 'Europe/Simferopol', 'Europe/Volgograd', 'Europe/Minsk'],
  name: 'Moscow Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Samara, Ulyanovsk, Saratov',
  value: 'Europe/Astrakhan',
  matchingValues: ['Europe/Astrakhan', 'Europe/Samara', 'Europe/Ulyanovsk'],
  name: 'Samara Time'
}, {
  offset: 3,
  observesDST: false,
  label: 'Damascus',
  value: 'Asia/Damascus',
  matchingValues: ['Asia/Damascus'],
  name: 'Syria Standard Time'
}, {
  offset: 3.5,
  observesDST: false,
  label: 'Tehran',
  value: 'Asia/Tehran',
  matchingValues: ['Asia/Tehran'],
  name: 'Iran Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Abu Dhabi, Muscat',
  value: 'Etc/GMT-4',
  matchingValues: ['Asia/Dubai', 'Asia/Muscat', 'Etc/GMT-4'],
  name: 'Arabian Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Baku',
  value: 'Asia/Baku',
  matchingValues: ['Asia/Baku'],
  name: 'Azerbaijan Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Port Louis',
  value: 'Indian/Mahe',
  matchingValues: ['Indian/Mahe', 'Indian/Mauritius', 'Indian/Reunion'],
  name: 'Mauritius Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Tbilisi',
  value: 'Asia/Tbilisi',
  matchingValues: ['Asia/Tbilisi'],
  name: 'Georgian Standard Time'
}, {
  offset: 4,
  observesDST: false,
  label: 'Yerevan',
  value: 'Asia/Yerevan',
  matchingValues: ['Asia/Yerevan'],
  name: 'Caucasus Standard Time'
}, {
  offset: 4.5,
  observesDST: false,
  label: 'Kabul',
  value: 'Asia/Kabul',
  matchingValues: ['Asia/Kabul'],
  name: 'Afghanistan Standard Time'
}, {
  offset: 5,
  observesDST: false,
  label: 'Ashgabat, Tashkent',
  value: 'Etc/GMT-5',
  matchingValues: ['Antarctica/Mawson', 'Asia/Aqtau', 'Asia/Aqtobe', 'Asia/Ashgabat', 'Asia/Dushanbe', 'Asia/Oral', 'Asia/Samarkand', 'Asia/Tashkent', 'Asia/Qyzylorda', 'Etc/GMT-5', 'Indian/Kerguelen', 'Indian/Maldives'],
  name: 'West Asia Standard Time'
}, {
  offset: 5,
  observesDST: false,
  label: 'Islamabad, Karachi',
  value: 'Asia/Karachi',
  matchingValues: ['Asia/Karachi'],
  name: 'Pakistan Standard Time'
}, {
  offset: 5.5,
  observesDST: false,
  label: 'Chennai, Kolkata, Mumbai, New Delhi',
  value: 'Asia/Kolkata',
  matchingValues: ['Asia/Kolkata', 'Asia/Calcutta'],
  name: 'India Standard Time'
}, {
  offset: 5.5,
  observesDST: false,
  label: 'Sri Jayawardenepura',
  value: 'Asia/Colombo',
  matchingValues: ['Asia/Colombo'],
  name: 'Sri Lanka Standard Time'
}, {
  offset: 5.75,
  observesDST: false,
  label: 'Kathmandu',
  value: 'Asia/Kathmandu',
  matchingValues: ['Asia/Kathmandu'],
  name: 'Nepal Standard Time'
}, {
  offset: 6,
  observesDST: false,
  label: 'Nur-Sultan (Astana)',
  value: 'Etc/GMT-6',
  matchingValues: ['Asia/Almaty', 'Asia/Bishkek', 'Asia/Urumqi', 'Etc/GMT-6', 'Indian/Chagos'],
  name: 'Central Asia Standard Time'
}, {
  offset: 6,
  observesDST: false,
  label: 'Dhaka',
  value: 'Asia/Dhaka',
  matchingValues: ['Asia/Dhaka', 'Asia/Thimphu'],
  name: 'Bangladesh Standard Time'
}, {
  offset: 5,
  observesDST: false,
  label: 'Ekaterinburg',
  value: 'Asia/Yekaterinburg',
  matchingValues: ['Asia/Yekaterinburg'],
  name: 'Ekaterinburg Standard Time'
}, {
  offset: 5,
  observesDST: false,
  label: 'Vostok Station',
  value: 'Antarctica/Vostok',
  matchingValues: ['Antarctica/Vostok'],
  name: 'Vostok Time'
}, {
  offset: 6.5,
  observesDST: false,
  label: 'Yangon (Rangoon)',
  value: 'Asia/Rangoon',
  matchingValues: ['Asia/Rangoon', 'Indian/Cocos'],
  name: 'Myanmar Standard Time'
}, {
  offset: 7,
  observesDST: false,
  label: 'Bangkok, Hanoi, Jakarta',
  value: 'Etc/GMT-7',
  matchingValues: ['Antarctica/Davis', 'Asia/Bangkok', 'Asia/Hovd', 'Asia/Jakarta', 'Asia/Phnom_Penh', 'Asia/Pontianak', 'Asia/Saigon', 'Asia/Vientiane', 'Etc/GMT-7', 'Indian/Christmas'],
  name: 'SE Asia Standard Time'
}, {
  offset: 7,
  observesDST: false,
  label: 'Novosibirsk',
  value: 'Asia/Novokuznetsk',
  matchingValues: ['Asia/Novokuznetsk', 'Asia/Novosibirsk'],
  name: 'N. Central Asia Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Beijing, Chongqing, Hong Kong, Urumqi',
  value: 'Asia/Hong_Kong',
  matchingValues: ['Asia/Hong_Kong', 'Asia/Macau', 'Asia/Shanghai'],
  name: 'China Standard Time'
}, {
  offset: 6,
  observesDST: false,
  label: 'Omsk',
  value: 'Asia/Omsk',
  matchingValues: ['Asia/Omsk'],
  name: 'Omsk Standard Time'
}, {
  offset: 7,
  observesDST: false,
  label: 'Krasnoyarsk',
  value: 'Asia/Krasnoyarsk',
  matchingValues: ['Asia/Krasnoyarsk'],
  name: 'North Asia Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Kuala Lumpur, Singapore',
  value: 'Etc/GMT-8',
  matchingValues: ['Asia/Brunei', 'Asia/Kuala_Lumpur', 'Asia/Kuching', 'Asia/Makassar', 'Asia/Manila', 'Asia/Singapore', 'Etc/GMT-8'],
  name: 'Singapore Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Taipei',
  value: 'Asia/Taipei',
  matchingValues: ['Asia/Taipei'],
  name: 'Taipei Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Ulaanbaatar',
  value: 'Asia/Choibalsan',
  matchingValues: ['Asia/Choibalsan', 'Asia/Ulaanbaatar'],
  name: 'Ulaanbaatar Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Irkutsk',
  value: 'Asia/Irkutsk',
  matchingValues: ['Asia/Irkutsk'],
  name: 'North Asia East Standard Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Casey',
  value: 'Antarctica/Casey',
  matchingValues: ['Antarctica/Casey'],
  name: 'Casey Time'
}, {
  offset: 8,
  observesDST: false,
  label: 'Perth',
  value: 'Australia/Perth',
  matchingValues: ['Australia/Perth'],
  name: 'W. Australia Standard Time'
}, {
  offset: 9,
  observesDST: false,
  label: 'Osaka, Sapporo, Tokyo',
  value: 'Etc/GMT-9',
  matchingValues: ['Asia/Dili', 'Asia/Jayapura', 'Asia/Tokyo', 'Etc/GMT-9', 'Pacific/Palau'],
  name: 'Tokyo Standard Time'
}, {
  offset: 9,
  observesDST: false,
  label: 'Seoul',
  value: 'Asia/Pyongyang',
  matchingValues: ['Asia/Pyongyang', 'Asia/Seoul'],
  name: 'Korea Standard Time'
}, {
  offset: 9.5,
  observesDST: true,
  label: 'Adelaide',
  value: 'Australia/Adelaide',
  matchingValues: ['Australia/Adelaide', 'Australia/Broken_Hill'],
  name: 'Cen. Australia Standard Time'
}, {
  offset: 9.5,
  observesDST: false,
  label: 'Darwin',
  value: 'Australia/Darwin',
  matchingValues: ['Australia/Darwin'],
  name: 'AUS Central Standard Time'
}, {
  offset: 10,
  observesDST: false,
  label: 'Brisbane',
  value: 'Australia/Brisbane',
  matchingValues: ['Australia/Brisbane', 'Australia/Lindeman'],
  name: 'E. Australia Standard Time'
}, {
  offset: 10,
  observesDST: true,
  label: 'Canberra, Melbourne, Sydney',
  value: 'Australia/Melbourne',
  matchingValues: ['Australia/Melbourne', 'Australia/Sydney'],
  name: 'AUS Eastern Standard Time'
}, {
  offset: 10,
  observesDST: false,
  label: 'Guam, Port Moresby',
  value: 'Etc/GMT-10',
  matchingValues: ['Antarctica/DumontDUrville', 'Etc/GMT-10', 'Pacific/Guam', 'Pacific/Port_Moresby', 'Pacific/Saipan', 'Pacific/Truk'],
  name: 'West Pacific Standard Time'
}, {
  offset: 10,
  observesDST: true,
  label: 'Hobart',
  value: 'Australia/Currie',
  matchingValues: ['Australia/Currie', 'Australia/Hobart', 'Antarctica/Macquarie'],
  name: 'Tasmania Standard Time'
}, {
  offset: 9,
  observesDST: false,
  label: 'Yakutsk',
  value: 'Asia/Chita',
  matchingValues: ['Asia/Chita', 'Asia/Khandyga', 'Asia/Yakutsk'],
  name: 'Yakutsk Standard Time'
}, {
  offset: 11,
  observesDST: false,
  label: 'Solomon Is., New Caledonia',
  value: 'Etc/GMT-11',
  matchingValues: ['Etc/GMT-11', 'Pacific/Efate', 'Pacific/Guadalcanal', 'Pacific/Kosrae', 'Pacific/Noumea', 'Pacific/Ponape'],
  name: 'Central Pacific Standard Time'
}, {
  offset: 10,
  observesDST: false,
  label: 'Vladivostok',
  value: 'Asia/Vladivostok',
  matchingValues: ['Asia/Ust-Nera', 'Asia/Vladivostok'],
  name: 'Vladivostok Standard Time'
}, {
  offset: 12,
  observesDST: true,
  label: 'Auckland, Wellington',
  value: 'Antarctica/McMurdo',
  matchingValues: ['Antarctica/McMurdo', 'Pacific/Auckland'],
  name: 'New Zealand Standard Time'
}, {
  offset: 12,
  observesDST: false,
  label: 'Fiji',
  value: 'Pacific/Fiji',
  matchingValues: ['Pacific/Fiji'],
  name: 'Fiji Standard Time'
}, {
  offset: 11,
  observesDST: false,
  label: 'Magadan',
  value: 'Asia/Magadan',
  matchingValues: ['Asia/Magadan', 'Asia/Srednekolymsk', 'Asia/Sakhalin'],
  name: 'Magadan Standard Time'
}, {
  offset: 12,
  observesDST: false,
  label: 'Petropavlovsk-Kamchatsky',
  value: 'Asia/Kamchatka',
  matchingValues: ['Asia/Anadyr', 'Asia/Kamchatka'],
  name: 'Kamchatka Standard Time'
}, {
  offset: 13,
  observesDST: false,
  label: "Nuku'alofa",
  value: 'Etc/GMT-13',
  matchingValues: ['Etc/GMT-13', 'Pacific/Enderbury', 'Pacific/Fakaofo', 'Pacific/Tongatapu'],
  name: 'Tonga Standard Time'
}, {
  offset: 13,
  observesDST: false,
  label: 'Samoa',
  value: 'Pacific/Apia',
  matchingValues: ['Pacific/Apia'],
  name: 'Samoa Standard Time'
}];
const LOCAL_TIME_ZONE_OFFSET = new Date().getTimezoneOffset();
const LOCAL_TIME_ZONE_OFFSET_CONSIDERING_DST = LOCAL_TIME_ZONE_OFFSET + (userHasDST ? 60 : 0);

// user local timezone must be always added AND FIRST in the timezone dropdown:
// either find it and re-sort the array, or add new timezone
// set it by 1) matching exact timezone name, using "matchingValues" array 2) matching timezone offset, 3) fallback custom timezone definition
let LOCAL_TIME_ZONE = RAW_TIME_ZONES.find(tz => tz.matchingValues.includes(moment.tz.guess())) || RAW_TIME_ZONES.find(tz => tz.offset === -LOCAL_TIME_ZONE_OFFSET_CONSIDERING_DST / 60) || {
  offset: -LOCAL_TIME_ZONE_OFFSET / 60,
  observesDST: userHasDST,
  value: moment.tz.guess(),
  label: moment.tz.guess(),
  name: moment.tz.guess(),
  matchingValues: [moment.tz.guess()]
};
if (RAW_TIME_ZONES.includes(LOCAL_TIME_ZONE)) {
  RAW_TIME_ZONES.splice(RAW_TIME_ZONES.indexOf(LOCAL_TIME_ZONE), 1);
}
RAW_TIME_ZONES.unshift(LOCAL_TIME_ZONE);

// format timezones for global use as select box options:
function formatTimezone(timezone, allowDST) {
  let {
    observesDST,
    label,
    value,
    matchingValues,
    name,
    offset
  } = timezone;
  let offsetWithDST = offset;
  if (allowDST && moment.tz(value).isDST()) {
    offsetWithDST = offset + 1;
  }
  let offsetLabel = timezoneOffsetLabel(offsetWithDST);
  return {
    value,
    matchingValues,
    name,
    label: `(${offsetLabel}) ${label}`,
    offset: offsetWithDST * -60,
    observesDST
  };
}
const SELECTABLE_TIME_ZONES = RAW_TIME_ZONES.map(tz => formatTimezone(tz, true));
LOCAL_TIME_ZONE = formatTimezone(LOCAL_TIME_ZONE, true);

// Test helper:
// allows to assign DST-less offset and label to SELECTABLE_TIME_ZONES entries: useful in test scenarios, where we don't want to use real DST
function DST_TOGGLE(enabled) {
  let newSelectableTimezones = RAW_TIME_ZONES.map(tz => formatTimezone(tz, enabled));
  SELECTABLE_TIME_ZONES.forEach(tz => {
    let updatedTZ = newSelectableTimezones.find(ntz => ntz.value === tz.value);
    if (updatedTZ) {
      Object.assign(tz, {
        offset: updatedTZ.offset,
        label: updatedTZ.label
      });
    }
  });
}
export { DST_TOGGLE, LOCAL_TIME_ZONE, SELECTABLE_TIME_ZONES };